/**
 * Dashboard.
 *
 * Built against taskdojo-app/src/screens/DashboardScreen.js, which is a
 * configurable widget board: twelve widgets, user-orderable, defaults in
 * DEFAULT_DASHBOARD_KEYS. This ports the first four of that default order --
 * Today, Task overview, Daily habits, Quick actions -- plus the header and
 * summary band that sit above the board on the phone.
 *
 * Widgets arrive as their screens do. All of the phone's default order is now
 * here bar the daily report, which is a Cloud Function's output rather than
 * anything the web can compute.
 *
 * Every widget that depends on data is conditional: today's mood and the budget
 * card only appear once there is something to show. A widget rendering a
 * convincing zero is worse than one that is absent, because the zero looks like
 * an answer.
 *
 * Two things are deliberately *not* copies of the phone:
 *
 *  - dates. The phone's header formats with an explicit en-US locale but its
 *    Today widget passes `undefined` and picks up the device locale, so the two
 *    disagree on the same screen. Everything here goes through
 *    ../../utils/datetime, which is pinned, so the web is at least
 *    self-consistent.
 *  - widget ordering and quick-action customisation. Both are stored on the
 *    device through AsyncStorage. The web has nowhere to read that from, so the
 *    order is the documented default and the quick actions are
 *    DEFAULT_QUICK_ACTIONS, minus any whose screen the web does not have.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { db } from '../../firebaseConfig';
import { useApp } from '../../contexts/AppContext';
import { useConnections } from '../../contexts/ConnectionsContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useScore } from '../../hooks/useScore';
import BeltBadge from '../common/BeltBadge';
import {
  ScreenHeader, SummaryBand, Card, SectionTitle, ProgressRing, raised, pressable,
} from '../dojo';
import { formatTime } from '../../utils/datetime';
import { dayKey } from '../../utils/score';
import { chipText, readableOn, MARK_MIN } from '../../utils/taskColor';
import {
  MOOD_OPTIONS, subscribeToBudgetCategories, subscribeToBudgetEntries, getBudgetSettings,
} from '../../services/firebaseService';

import { DEFAULT_AFFIRMATIONS, dailyIndex } from '../../constants/affirmations';
import type { Task, Habit } from '../../services/firebaseService';

const CURRENCY_SYMBOLS: Record<string, string> = {
  GBP: '£', USD: '$', EUR: '€', AUD: 'A$', CAD: 'C$', JPY: '¥', INR: '₹',
};

export type ScreenKey =
  | 'dashboard' | 'tasks' | 'gantt' | 'calendar' | 'connections' | 'sensei'
  | 'habits' | 'lists' | 'mood' | 'affirmations' | 'notes' | 'budget'
  | 'support' | 'analytics' | 'admin' | 'settings';

interface DashboardProps {
  onNavigate?: (screen: ScreenKey) => void;
}

const isDone = (t: any) => !!(t?.completed || t?.status === 'completed');

/**
 * The phone's DEFAULT_QUICK_ACTIONS, in order, with the colours from
 * constants/dashboardConfig.js. MORE_ACTIONS is the second row: tiles for
 * screens that did not exist when this was first built, including log_mood,
 * which was dropped at the time because it led nowhere.
 */
const QUICK_ACTIONS: Array<{
  key: string; label: string; color: string; screen: ScreenKey; icon: React.ReactNode;
}> = [
  {
    key: 'add_task', label: 'Add task', color: '#4A6FA5', screen: 'tasks',
    icon: <path d="M12 8v8M8 12h8M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20z" strokeLinecap="round" />,
  },
  {
    key: 'habits', label: 'Mark habits', color: '#C1763C', screen: 'habits',
    icon: <path d="M12 2s4 4 4 8a4 4 0 0 1-8 0c0-1.5.5-2.5 1-3.5C9.5 8.5 12 7 12 2z M7 14a5 5 0 0 0 10 0c0 4-2 8-5 8s-5-4-5-8z" strokeLinecap="round" strokeLinejoin="round" />,
  },
  {
    key: 'calendar', label: 'Calendar', color: '#8E5572', screen: 'calendar',
    icon: <><rect x="3" y="4" width="18" height="18" rx="2" /><path d="M16 2v4M8 2v4M3 10h18" strokeLinecap="round" /></>,
  },
  {
    key: 'lists', label: 'View lists', color: '#3E8C84', screen: 'lists',
    icon: <path d="M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01" strokeLinecap="round" />,
  },
];

/** A second row, for the screens that exist now but did not when this was built. */
const MORE_ACTIONS: Array<{
  key: string; label: string; color: string; screen: ScreenKey; icon: React.ReactNode;
}> = [
  {
    key: 'log_mood', label: 'Log mood', color: '#E8B33A', screen: 'mood',
    icon: <><circle cx="12" cy="12" r="10" /><path d="M8 14s1.5 2 4 2 4-2 4-2M9 9h.01M15 9h.01" strokeLinecap="round" /></>,
  },
  {
    key: 'new_note', label: 'New note', color: '#8E5572', screen: 'notes',
    icon: <><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" strokeLinecap="round" strokeLinejoin="round" /><path d="M14 2v6h6" strokeLinecap="round" strokeLinejoin="round" /></>,
  },
  {
    key: 'budget', label: 'Add spend', color: '#4C9A2A', screen: 'budget',
    icon: <><path d="M12 1v22" strokeLinecap="round" /><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" strokeLinecap="round" /></>,
  },
  {
    key: 'analytics', label: 'Analytics', color: '#5B8FD6', screen: 'analytics',
    icon: <path d="M3 3v18h18M7 15l4-4 3 3 5-6" strokeLinecap="round" strokeLinejoin="round" />,
  },
];

const Dashboard: React.FC<DashboardProps> = ({ onNavigate }) => {
  const { state, getTodayCompletions } = useApp();
  const { connections } = useConnections();
  const { colors, isDark } = useTheme();
  /** The surface marks and chips sit on, which is what contrast is measured against. */
  const card = isDark ? '#1B2A32' : '#FFFFFF';

  const score = useScore();

  const tasks = (state.tasks || []) as Task[];
  const habits = (state.habits || []) as Habit[];
  const go = (s: ScreenKey) => onNavigate?.(s);

  const [notes, setNotes] = useState<any[]>([]);
  const [affirmations, setAffirmations] = useState<string[]>([]);
  const [todayMoods, setTodayMoods] = useState<any[]>([]);
  const [budget, setBudget] = useState<{ spent: number; total: number; symbol: string } | null>(null);

  // Pinned notes and the affirmation rotation are read here rather than from
  // AppContext, which does not carry either.
  useEffect(() => {
    const uid = state.user?.uid;
    if (!uid) return;
    const unsubN = onSnapshot(
      query(collection(db, 'notes'), where('userId', '==', uid)),
      (snap) => setNotes(snap.docs.map((d) => ({ id: d.id, ...d.data() }))
        .filter((n: any) => n.isFavorite)),
      () => {},
    );
    const unsubA = onSnapshot(
      query(collection(db, 'affirmations'), where('userId', '==', uid)),
      (snap) => setAffirmations(snap.docs.map((d) => (d.data() as any).text).filter(Boolean)),
      () => {},
    );
    // Today's feelings, and this month's spend against budget.
    const todayKey = dayKey(new Date());
    const unsubM = onSnapshot(
      collection(db, 'users', uid, 'feelings'),
      (snap) => setTodayMoods(
        snap.docs.map((d) => d.data() as any).filter((m) => m.date === todayKey),
      ),
      () => {},
    );

    let cats: any[] = [];
    let entries: any[] = [];
    let settings: { overallBudget: number; currency: string } = { overallBudget: 0, currency: 'GBP' };

    const recompute = () => {
      const now = new Date();
      const spent = entries
        .filter((e) => {
          const d = new Date(e.date);
          return !Number.isNaN(d.getTime())
            && d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth();
        })
        .reduce((s2, e) => s2 + (e.amount || 0), 0);
      // Explicit overall budget wins; the category sum is only a fallback.
      const total = settings.overallBudget > 0
        ? settings.overallBudget
        : cats.reduce((s2, c) => s2 + (c.budgetAmount || 0), 0);
      setBudget(total > 0 || spent > 0
        ? { spent, total, symbol: CURRENCY_SYMBOLS[settings.currency] || '£' }
        : null);
    };

    const unsubBC = subscribeToBudgetCategories(uid, (c) => { cats = c; recompute(); });
    const unsubBE = subscribeToBudgetEntries(uid, (e) => { entries = e; recompute(); });
    getBudgetSettings(uid).then((s2) => {
      if (s2) settings = { overallBudget: s2.overallBudget || 0, currency: s2.currency || 'GBP' };
      recompute();
    }).catch(() => {});

    return () => { unsubN(); unsubA(); unsubM(); unsubBC(); unsubBE(); };
  }, [state.user?.uid]);

  // Same rotation as the Affirmations screen and the phone, so all three agree
  // on what today's line is.
  const affirmationPool = affirmations.length > 0 ? affirmations : DEFAULT_AFFIRMATIONS;
  const todaysAffirmation = affirmationPool[dailyIndex(affirmationPool.length)];

  const doneHabitIds = getTodayCompletions();
  const habitDone = (id: string) => doneHabitIds.includes(id);
  const habitsDone = habits.filter((h) => habitDone(h.id)).length;

  /**
   * The task figures behind the board. Mobile computes these across several
   * places with slightly different rules; they are derived once here so the
   * summary band, the Today card and the overview cannot contradict each other.
   */
  const stats = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    const open = tasks.filter((t) => !isDone(t) && t.status !== 'declined');
    const completed = tasks.filter(isDone);

    const onDay = (v: string | null) => {
      if (!v) return false;
      const d = new Date(v);
      return !Number.isNaN(d.getTime()) && d >= today && d < tomorrow;
    };

    // The phone's Today widget counts a task if *either* its deadline or its
    // start falls today -- a task you planned to start today belongs on today's
    // list even when it is not due until Friday.
    const dueToday = tasks.filter(
      (t) => !isDone(t) && (onDay(t.deadline) || onDay(t.startDateTime)),
    );

    const overdue = tasks.filter((t) => {
      if (isDone(t) || !t.deadline) return false;
      const d = new Date(t.deadline);
      return !Number.isNaN(d.getTime()) && d < today;
    });

    const rate = tasks.length > 0 ? Math.round((completed.length / tasks.length) * 100) : 0;

    return { open, completed, dueToday, overdue, rate };
  }, [tasks]);

  const firstName = state.user?.displayName?.split(' ')[0] || 'there';

  // Pinned to en-US, matching the phone's header. Its Today widget uses the
  // device locale instead, so the phone disagrees with itself; the web does not.
  const dateLine = new Date().toLocaleDateString('en-US', {
    weekday: 'long', month: 'long', day: 'numeric',
  });

  const habitRate = habits.length > 0 ? (habitsDone / habits.length) * 100 : 0;
  const habitsLeft = habits.length - habitsDone;

  return (
    <div className={`min-h-screen ${colors.bg} ${colors.text}`}>
      <div className="max-w-3xl mx-auto pt-4 pb-24">
        <ScreenHeader
          title={`Hello, ${firstName}! 👋`}
          subtitle={dateLine}
          right={<BeltBadge score={score.score} size={44} />}
        />

        {/* The day stated in numbers, so the cards below are detail rather than
            the only way to find out how you are doing. */}
        <SummaryBand
          items={[
            { value: `${habitsDone}/${habits.length}`, label: 'Habits today' },
            { value: stats.open.length, label: 'Open tasks', color: '#C08A1E' },
          ]}
        />

        <div className="px-5 space-y-5">
          {/* ------------------------------ Today ------------------------------ */}
          <Card className="p-4">
            <SectionTitle
              right={
                <button
                  type="button"
                  onClick={() => go('calendar')}
                  className="px-[10px] py-1 rounded-full text-[12px] font-extrabold
                    bg-dojo-red/15 text-dojo-red hover:bg-dojo-red/25 transition-colors"
                >
                  Calendar
                </button>
              }
            >
              Today
            </SectionTitle>

            {stats.dueToday.length === 0 && habits.length === 0 ? (
              <p className={`text-[14px] font-semibold mt-3 ${colors.textSecondary}`}>
                Nothing scheduled for today.
              </p>
            ) : (
              <ul className="mt-3 space-y-2 list-none p-0 m-0">
                {stats.dueToday.slice(0, 3).map((t) => (
                  <li key={t.id}>
                    <button
                      type="button"
                      onClick={() => go('tasks')}
                      className={`w-full flex items-center gap-3 text-left rounded-[12px] px-3 py-2
                        ${isDark ? 'hover:bg-dojo-surfaceDeep' : 'hover:bg-stone-100'} transition-colors`}
                    >
                      <span
                        className="w-[6px] h-[26px] rounded-[3px] flex-shrink-0"
                        style={{ backgroundColor: readableOn(t.color || '#4A6FA5', card, MARK_MIN) }}
                      />
                      <span className="flex-1 min-w-0 text-[14px] font-bold truncate">{t.title}</span>
                      {t.deadline && (
                        <span className={`text-[12px] font-semibold flex-shrink-0 ${colors.textSecondary}`}>
                          {formatTime(t.deadline)}
                        </span>
                      )}
                    </button>
                  </li>
                ))}

                {/* Habits fill whatever room the tasks leave, to a total of
                    four -- the phone's rule, so the card is a consistent height
                    rather than growing with the day. */}
                {habits.slice(0, Math.max(0, 4 - Math.min(3, stats.dueToday.length))).map((h) => {
                  const done = habitDone(h.id);
                  return (
                    <li key={h.id}>
                      <button
                        type="button"
                        onClick={() => go('habits')}
                        className={`w-full flex items-center gap-3 text-left rounded-[12px] px-3 py-2
                          ${isDark ? 'hover:bg-dojo-surfaceDeep' : 'hover:bg-stone-100'} transition-colors`}
                      >
                        <span className="text-[17px] leading-none flex-shrink-0">{h.icon || '🎯'}</span>
                        <span className={`flex-1 min-w-0 text-[14px] font-bold truncate
                          ${done ? 'line-through opacity-50' : ''}`}>
                          {h.title}
                        </span>
                        {done && (
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                            stroke="#4C9A2A" strokeWidth="3" className="flex-shrink-0">
                            <path d="M20 6L9 17l-5-5" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        )}
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </Card>

          {/* -------------------------- Task overview -------------------------- */}
          <Card className="p-4">
            <SectionTitle
              right={
                <button
                  type="button"
                  onClick={() => go('tasks')}
                  className="px-[10px] py-1 rounded-full text-[12px] font-extrabold
                    bg-dojo-red/15 text-dojo-red hover:bg-dojo-red/25 transition-colors"
                >
                  View all
                </button>
              }
            >
              Task overview
            </SectionTitle>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4">
              {[
                { label: 'Completed', value: stats.completed.length, color: '#4C9A2A' },
                { label: 'Active', value: stats.open.length, color: '#C1763C' },
                { label: 'Today', value: stats.dueToday.length, color: '#4A6FA5' },
                { label: 'Overdue', value: stats.overdue.length, color: '#E34234' },
              ].map((s) => (
                <div
                  key={s.label}
                  className={`rounded-[12px] border ${colors.border} ${isDark ? 'bg-dojo-surfaceDeep' : 'bg-stone-50'}
                    px-3 py-3 flex flex-col items-center`}
                >
                  <span
                    className="text-[22px] font-black tracking-[-0.6px] tabular-nums"
                    style={{ color: s.color }}
                  >
                    {s.value}
                  </span>
                  <span className={`text-[11px] font-bold uppercase tracking-[0.4px] mt-0.5 ${colors.textSecondary}`}>
                    {s.label}
                  </span>
                </div>
              ))}
            </div>

            <div className={`mt-4 pt-4 border-t ${colors.border}`}>
              <div className="flex items-center justify-between mb-2">
                <span className="text-[13px] font-extrabold">Completion rate</span>
                <span className="text-[13px] font-extrabold tabular-nums">{stats.rate}%</span>
              </div>
              <div className={`h-2 rounded-full overflow-hidden ${isDark ? 'bg-dojo-surfaceDeep' : 'bg-stone-200'}`}>
                <div
                  className="h-full rounded-full bg-dojo-green transition-[width] duration-700"
                  style={{ width: `${stats.rate}%` }}
                />
              </div>
            </div>
          </Card>

          {/* --------------------------- Daily habits --------------------------- */}
          <Card className="p-4">
            <SectionTitle
              right={
                <button
                  type="button"
                  onClick={() => go('habits')}
                  className="px-[10px] py-1 rounded-full text-[12px] font-extrabold
                    bg-dojo-red/15 text-dojo-red hover:bg-dojo-red/25 transition-colors"
                >
                  Open
                </button>
              }
            >
              Daily habits
            </SectionTitle>

            {habits.length === 0 ? (
              <div className="flex flex-col items-start gap-3 mt-3">
                <p className={`text-[14px] font-semibold ${colors.textSecondary}`}>
                  No habits yet.
                </p>
                <button
                  type="button"
                  onClick={() => go('habits')}
                  className={`rounded-[12px] bg-dojo-red border-dojo-redDeep text-white ${raised} ${pressable}
                    px-4 pt-[10px] pb-2 text-[14px] font-extrabold`}
                >
                  Create your first habit
                </button>
              </div>
            ) : (
              <>
                <div className="flex items-center gap-4 mt-4">
                  <ProgressRing percent={habitRate} sublabel="today" />
                  <div className="min-w-0">
                    <p className="text-[17px] font-extrabold tracking-[-0.3px]">
                      {habitsDone} of {habits.length} done
                    </p>
                    <p className={`text-[13px] font-semibold mt-0.5 ${colors.textSecondary}`}>
                      {habitsLeft === 0
                        ? 'All habits complete — nice.'
                        : `${habitsLeft} still open today`}
                    </p>
                  </div>
                </div>

                {/* Chips, scannable in one glance rather than read top to
                    bottom -- the phone's treatment. */}
                <div className="flex gap-2 mt-4 overflow-x-auto pb-1">
                  {habits.map((h) => {
                    const done = habitDone(h.id);
                    return (
                      <button
                        key={h.id}
                        type="button"
                        onClick={() => go('habits')}
                        className={`flex-shrink-0 w-[86px] rounded-[12px] border-2 px-2 py-2.5
                          flex flex-col items-center gap-1 transition-colors
                          ${done
                            ? 'bg-dojo-red/[0.13] border-dojo-red'
                            : `${isDark ? 'bg-dojo-surfaceDeep border-slate-700' : 'bg-stone-50 border-stone-300'}`}`}
                      >
                        <span className="text-[19px] leading-none">{h.icon || '🎯'}</span>
                        <span className={`text-[12px] font-extrabold tabular-nums
                          ${h.streak > 0 ? 'text-dojo-clay' : colors.textSecondary}`}>
                          {h.streak > 0 ? `🔥 ${h.streak}` : '—'}
                        </span>
                        <span className={`text-[11px] font-semibold truncate max-w-full ${colors.textSecondary}`}>
                          {h.title}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </>
            )}
          </Card>

          {/* ------------------------------ Feelings ------------------------------ */}
          <Card onClick={() => go('mood')} className="p-4 text-left">
            <span className="flex items-center justify-between mb-2">
              <span className={`text-[11px] font-bold uppercase tracking-[0.6px] ${colors.textMuted}`}>
                Today's mood
              </span>
              <span className="px-[10px] py-1 rounded-full text-[12px] font-extrabold
                bg-dojo-red/15 text-dojo-red">
                {todayMoods.length > 0 ? 'Log another' : 'Log one'}
              </span>
            </span>
            {todayMoods.length === 0 ? (
              <span className={`block text-[14px] font-semibold ${colors.textSecondary}`}>
                Nothing logged today.
              </span>
            ) : (
              <span className="flex flex-wrap items-center gap-2">
                {todayMoods.slice(0, 6).map((m: any, i: number) => {
                  const all = [...(MOOD_OPTIONS as any[]),
                    ...(((state.user as any)?.customMoods || []) as any[])];
                  const o = all.find((x) => x.id === m.feeling);
                  return (
                    <span
                      key={i}
                      className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[12.5px] font-bold"
                      style={{
                        color: chipText(o?.color || '#8E8E93', card),
                        backgroundColor: `${o?.color || '#8E8E93'}20`,
                      }}
                    >
                      <span className="text-[14px] leading-none">{o?.emoji || '🌀'}</span>
                      {o?.label || 'Logged'}
                    </span>
                  );
                })}
                {todayMoods.length > 6 && (
                  <span className={`text-[12px] font-semibold ${colors.textSecondary}`}>
                    +{todayMoods.length - 6}
                  </span>
                )}
              </span>
            )}
          </Card>

          {/* ------------------------------- Budget ------------------------------- */}
          {budget && (
            <Card onClick={() => go('budget')} className="p-4 text-left">
              <span className="flex items-center justify-between mb-2">
                <span className={`text-[11px] font-bold uppercase tracking-[0.6px] ${colors.textMuted}`}>
                  This month
                </span>
                <span className="px-[10px] py-1 rounded-full text-[12px] font-extrabold
                  bg-dojo-red/15 text-dojo-red">
                  Budget
                </span>
              </span>
              <span className="flex items-baseline gap-2 mb-2">
                <span className="text-[24px] font-black tracking-[-0.7px] tabular-nums">
                  {budget.symbol}{budget.spent.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
                <span className={`text-[13px] font-semibold ${colors.textSecondary}`}>
                  of {budget.symbol}{budget.total.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
              </span>
              <span className={`block h-2 rounded-full overflow-hidden
                ${isDark ? 'bg-dojo-surfaceDeep' : 'bg-stone-200'}`}>
                <span
                  className="block h-full rounded-full"
                  style={{
                    width: `${budget.total > 0 ? Math.min((budget.spent / budget.total) * 100, 100) : 0}%`,
                    backgroundColor: budget.total > 0 && budget.spent > budget.total ? '#E34234' : '#4C9A2A',
                  }}
                />
              </span>
            </Card>
          )}

          {/* ------------------------ Daily affirmation ------------------------ */}
          <Card onClick={() => go('affirmations')} className="p-4 text-left">
            <span className={`block text-[11px] font-bold uppercase tracking-[0.6px] mb-2
              ${colors.textMuted}`}>
              Daily affirmation
            </span>
            <span className="block text-[17px] font-extrabold tracking-[-0.3px] leading-snug">
              {todaysAffirmation}
            </span>
          </Card>

          {/* --------------------------- Pinned notes --------------------------- */}
          {notes.length > 0 && (
            <Card className="p-4">
              <SectionTitle
                right={
                  <button
                    type="button"
                    onClick={() => go('notes')}
                    className="px-[10px] py-1 rounded-full text-[12px] font-extrabold
                      bg-dojo-red/15 text-dojo-red hover:bg-dojo-red/25 transition-colors"
                  >
                    All notes
                  </button>
                }
              >
                Pinned notes
              </SectionTitle>
              <ul className="mt-3 space-y-2 list-none p-0 m-0">
                {notes.slice(0, 3).map((n: any) => (
                  <li key={n.id}>
                    <button
                      type="button"
                      onClick={() => go('notes')}
                      className={`w-full text-left rounded-[12px] px-3 py-2
                        ${isDark ? 'hover:bg-dojo-surfaceDeep' : 'hover:bg-stone-100'} transition-colors`}
                    >
                      <span className="block text-[14px] font-bold truncate">
                        {n.title || 'Untitled'}
                      </span>
                      {n.content && (
                        <span className={`block text-[12.5px] truncate ${colors.textSecondary}`}>
                          {n.content}
                        </span>
                      )}
                    </button>
                  </li>
                ))}
              </ul>
              {notes.length > 3 && (
                <p className={`text-[12px] font-semibold mt-2 ${colors.textSecondary}`}>
                  and {notes.length - 3} more pinned
                </p>
              )}
            </Card>
          )}

          {/* --------------------------- Quick actions --------------------------- */}
          <div>
            <SectionTitle className="mb-3">Quick actions</SectionTitle>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {[...QUICK_ACTIONS, ...MORE_ACTIONS].map((a) => (
                <button
                  key={a.key}
                  type="button"
                  onClick={() => go(a.screen)}
                  className={`rounded-[15px] ${raised} ${pressable} text-white
                    px-3 pt-4 pb-3 flex flex-col items-center gap-2`}
                  style={{ backgroundColor: a.color, borderColor: 'rgba(0,0,0,0.28)' }}
                >
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none"
                    stroke="currentColor" strokeWidth="2">
                    {a.icon}
                  </svg>
                  <span className="text-[13px] font-extrabold">{a.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* ---------------------------- Quick stats ---------------------------- */}
          <div>
            <SectionTitle className="mb-3">Quick stats</SectionTitle>
            <div className="grid grid-cols-3 gap-3">
              {[
                { label: 'daily habits', value: habits.length, screen: 'habits' as ScreenKey },
                { label: 'open tasks', value: stats.open.length, screen: 'tasks' as ScreenKey },
                { label: 'connections', value: connections.length, screen: 'connections' as ScreenKey },
              ].map((s) => (
                <Card key={s.label} onClick={() => go(s.screen)} className="px-3 py-3">
                  <div className="flex flex-col items-center">
                    {/* The bright gold is for dark grounds only; on a white
                        card it is barely darker than the card. */}
                    <span
                      className="text-[20px] font-black tracking-[-0.5px] tabular-nums"
                      style={{ color: isDark ? '#FEC200' : '#C08A1E' }}
                    >
                      {s.value}
                    </span>
                    <span className={`text-[11px] font-semibold mt-0.5 text-center ${colors.textSecondary}`}>
                      {s.label}
                    </span>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
